import { useId, useState } from 'react'
import toast from 'react-hot-toast'
import type { DigestStatus, PublicCallReceipt } from '../../types'
import { formatUtcSeconds } from '../../utils/utcTime'

/** "0892ff7c51d1…0d24": enough to compare by eye; the full hash copies. */
export const shortHash = (hash: string) => `${hash.slice(0, 12)}…${hash.slice(-4)}`

/** "Oct 5, 2026" from an ISO timestamp, as a UTC calendar date. */
const utcDate = (iso: string) =>
  new Date(/(Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })

const copy = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text)
    toast.success('Hash copied')
  } catch {
    toast.error('Could not copy the hash')
  }
}

/** "· digest of 2026-10-03, public ↗" -- or that it is not public yet. */
const DigestNote = ({ day, status, testId }: { day: string | null; status?: DigestStatus | null; testId: string }) => {
  if (!day) return null
  return (
    <span data-testid={testId}>
      {` · digest of ${day}`}
      {status?.state === 'pushed' && status.url ? (
        <>
          {', '}
          <a href={status.url} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
            public ↗
          </a>
        </>
      ) : status?.state === 'built' ? (
        ', not yet public'
      ) : (
        ', published after that day closes (UTC)'
      )}
    </span>
  )
}

const Hash = ({ hash, label }: { hash: string; label: string }) => (
  <span className="inline-flex items-center gap-2">
    <code className="text-gray-100" title={hash} data-testid={`${label}-hash`}>
      {shortHash(hash)}
    </code>
    <button type="button" onClick={() => copy(hash)} className="text-xs text-gray-400 hover:text-gray-200">
      Copy
    </button>
  </span>
)

/**
 * The receipt line on a call's page: its SHA-256 fingerprints, when they were
 * taken, which day's public digest lists them, and -- behind an expander --
 * the exact bytes that were hashed, so a reader can recompute them.
 */
const CallReceipt = ({ receipt }: { receipt: PublicCallReceipt | null }) => {
  const [open, setOpen] = useState(false)
  const bytesId = useId()
  if (!receipt) return <span className="text-gray-400">Not yet fingerprinted</span>

  return (
    <div className="flex flex-col items-end gap-1 text-right" data-testid="receipt">
      <span>
        Call <Hash hash={receipt.call_hash} label="call" />
      </span>
      <span className="text-xs text-gray-400" data-testid="receipt-when">
        {receipt.fingerprinted_after_publish
          ? `fingerprinted ${utcDate(receipt.call_hashed_at)}; published earlier`
          : `fingerprinted at publish, ${formatUtcSeconds(receipt.call_hashed_at)}`}
        <DigestNote day={receipt.call_digest_day} status={receipt.call_digest} testId="receipt-call-digest" />
      </span>
      {receipt.outcome_hash && (
        <>
          <span>
            Outcome <Hash hash={receipt.outcome_hash} label="outcome" />
          </span>
          {receipt.outcome_hashed_at && (
            <span className="text-xs text-gray-400" data-testid="receipt-outcome-when">
              {receipt.outcome_basis === 'backfill'
                ? `fingerprinted ${utcDate(receipt.outcome_hashed_at)}; closed earlier`
                : `fingerprinted at close, ${formatUtcSeconds(receipt.outcome_hashed_at)}`}
              <DigestNote day={receipt.outcome_digest_day} status={receipt.outcome_digest} testId="receipt-outcome-digest" />
            </span>
          )}
        </>
      )}
      {receipt.call_payload && (
        <>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-controls={bytesId}
            className="text-xs text-accent hover:underline"
          >
            {open ? 'Hide what was hashed ↑' : 'Show what was hashed ↓'}
          </button>
          {open && (
            <div id={bytesId} className="w-full text-left" data-testid="receipt-bytes">
              <p className="mt-1 text-xs text-gray-400">SHA-256 of these exact bytes (UTF-8) is the hash above:</p>
              <pre className="mt-1 whitespace-pre-wrap break-all rounded bg-terminal-bg p-2 text-[11px] text-gray-300">
                {receipt.call_payload}
              </pre>
              {receipt.outcome_payload && (
                <pre className="mt-1 whitespace-pre-wrap break-all rounded bg-terminal-bg p-2 text-[11px] text-gray-300">
                  {receipt.outcome_payload}
                </pre>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default CallReceipt
